/* eslint-disable react-refresh/only-export-components */
// src/context/LocationContext.jsx
import { createContext, useContext, useState, useCallback } from 'react';

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_LOCATIONS = [
  {
    id: 'loc-001',
    name: 'Main Street Shop',
    address: '3129 Main St',
    city: 'West Hollywood',
    state: 'CA',
    zip: '90046',
    phone: '(310) 555-0100',
    color: '#2E8BF0',
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'loc-002',
    name: 'Downtown Studio',
    address: '800 S Figueroa St',
    city: 'Los Angeles',
    state: 'CA',
    zip: '90017',
    phone: '(213) 555-0200',
    color: '#10B981',
    createdAt: '2024-06-01T00:00:00Z',
  },
];

function loadLocations() {
  if (import.meta.env.VITE_DEV_AUTH === '1') return SEED_LOCATIONS;
  try {
    const raw = localStorage.getItem('wm-locations');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return SEED_LOCATIONS;
}

function saveLocations(locations) {
  try { localStorage.setItem('wm-locations', JSON.stringify(locations)); } catch { /* ignore */ }
}

function loadActiveId(locations) {
  try {
    const stored = localStorage.getItem('wm-active-location');
    if (stored === 'all') return 'all';
    if (stored && locations.find(l => l.id === stored)) return stored;
  } catch { /* ignore */ }
  return locations[0]?.id || 'all';
}

// ─── Context ──────────────────────────────────────────────────────────────────
const LocationContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export function LocationProvider({ children }) {
  const [locations, setLocations] = useState(loadLocations);
  const [activeLocationId, setActiveLocationIdState] = useState(() =>
    loadActiveId(loadLocations())
  );

  const setActiveLocation = useCallback((id) => {
    setActiveLocationIdState(id);
    try { localStorage.setItem('wm-active-location', id); } catch { /* ignore */ }
  }, []);

  const addLocation = useCallback((data) => {
    const newLoc = {
      id: `loc-${Date.now()}`,
      createdAt: new Date().toISOString(),
      color: '#2E8BF0',
      ...data,
    };
    setLocations(prev => {
      const next = [...prev, newLoc];
      saveLocations(next);
      return next;
    });
    return newLoc;
  }, []);

  const updateLocation = useCallback((id, patch) => {
    setLocations(prev => {
      const next = prev.map(l => (l.id === id ? { ...l, ...patch } : l));
      saveLocations(next);
      return next;
    });
  }, []);

  const deleteLocation = useCallback((id) => {
    setLocations(prev => {
      const next = prev.filter(l => l.id !== id);
      saveLocations(next);
      // If deleted the active location, fall back to first
      if (activeLocationId === id) {
        const fallback = next[0]?.id || 'all';
        setActiveLocation(fallback);
      }
      return next;
    });
  }, [activeLocationId, setActiveLocation]);

  const activeLocation = activeLocationId === 'all'
    ? null
    : locations.find(l => l.id === activeLocationId) || null;

  const isAllLocations = activeLocationId === 'all';

  return (
    <LocationContext.Provider value={{
      locations,
      activeLocationId,
      activeLocation,
      isAllLocations,
      setActiveLocation,
      addLocation,
      updateLocation,
      deleteLocation,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useLocations() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocations must be used within LocationProvider');
  return ctx;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Returns the nickname if set, otherwise falls back to the shop name.
// Use this everywhere a location name is displayed to the user.
export function getDisplayName(loc) {
  if (!loc) return '';
  return loc.nickname?.trim() || loc.name;
}
